/**
 * AI Form Service - Conversational form filling with OpenAI
 *
 * Responsible for:
 * 1. Resolving dynamic options (roles, departments, locations) from APIs
 * 2. Calling backend /chat/ai/form-assist with conversation history
 * 3. Parsing AI responses and extracting structured data
 * 4. Mapping simplified permission levels to hierarchical permissions
 */

import { api, apiClient } from './api';
import { AiFormFieldMeta, AiFormMetadata, AiFormTurn } from '@/scripts/userflow';

interface FormAssistRequest {
  formId: string;
  fields: AiFormFieldMeta[];
  turns: AiFormTurn[];
  collectedData: Record<string, any>;
  userContext: {
    userName: string;
    userRole: string;
    tenantName: string;
  };
}

interface FormAssistResponse {
  isSuccess: boolean;
  data: {
    message: string;
  };
  message: string;
}

/**
 * Resolve dynamic option keys (roles, departments, locations) from APIs
 * Returns a map of optionKey -> array of { value, label }
 */
export async function resolveFormOptions(
  fields: AiFormFieldMeta[]
): Promise<Record<string, { value: string; label: string }[]>> {
  const resolved: Record<string, { value: string; label: string }[]> = {};

  // Collect all unique dynamic option keys
  const dynamicKeys = new Set<string>();
  fields.forEach(field => {
    if (field.dynamicOptionsKey) {
      dynamicKeys.add(field.dynamicOptionsKey);
    }
  });

  // Fetch each dynamic option type in parallel
  const fetchPromises: Promise<void>[] = [];

  if (dynamicKeys.has('roles')) {
    fetchPromises.push(
      api.getRoles().then(response => {
        if (response?.isSuccess && response.data) {
          resolved.roles = response.data.map((role: any) => ({
            value: role.id,
            label: role.name,
          }));
        }
      })
    );
  }

  if (dynamicKeys.has('departments')) {
    fetchPromises.push(
      api.getDepartments?.().then((response: any) => {
        if (response?.isSuccess && response.data) {
          resolved.departments = response.data.map((dept: any) => ({
            value: dept.id,
            label: dept.name,
          }));
        }
      }).catch(() => {
        // Gracefully handle if getDepartments doesn't exist
        resolved.departments = [];
      })
    );
  }

  if (dynamicKeys.has('locations')) {
    fetchPromises.push(
      api.getLocations?.().then((response: any) => {
        if (response?.isSuccess && response.data) {
          resolved.locations = response.data.map((loc: any) => ({
            value: loc.id,
            label: `${loc.city}, ${loc.state || loc.country}`,
          }));
        }
      }).catch(() => {
        resolved.locations = [];
      })
    );
  }

  await Promise.all(fetchPromises);
  return resolved;
}

/**
 * Call the backend /chat/ai/form-assist endpoint
 * Returns the AI's next message string
 */
export async function callFormAssistAI(params: FormAssistRequest): Promise<string> {
  try {
    console.log('[aiFormService] Calling form assist AI for form:', params.formId);

    const response = await apiClient.post<FormAssistResponse>(
      '/chat/ai/form-assist',
      params
    );

    // The backend returns { isSuccess, data: { message: ... }, message: 'OK' }
    // The apiClient wrapper puts the entire backend response in response.data
    // So we need to read response.data.data.message to get the actual AI message
    const backendMessage = response?.data?.data?.message;

    console.log('[aiFormService] Response received:', {
      isSuccess: response?.isSuccess,
      hasData: !!response?.data,
      messageLength: backendMessage?.length || 0,
      messagePreview: backendMessage?.substring(0, 50) || '(empty)',
      fullBackendResponse: response?.data,
    });

    if (response?.isSuccess && backendMessage) {
      console.log('[aiFormService] Returning AI message:', backendMessage.substring(0, 100));
      return backendMessage;
    }

    if (!response?.isSuccess) {
      console.error('[aiFormService] API call failed:', response);
      return response?.data?.data?.message || `Error: ${response?.message || 'Failed to get AI response'}`;
    }

    console.warn('[aiFormService] No message in response:', response);
    return `Error: ${response?.message || 'No response from AI'}`;
  } catch (error: any) {
    console.error('[aiFormService] Error calling form assist AI:', error);
    return `Error: ${error.message || 'Network error - check your API connection'}`;
  }
}

/**
 * Map simplified permission levels to hierarchical permissions structure
 * Used for Phase 1 role creation where we simplify permissions to 4 levels
 */
export function mapSimplifiedPermissionsToHierarchical(
  level: string
): Record<string, any> {
  const mappings: Record<string, Record<string, any>> = {
    view: {
      // View-only permissions - read access to most areas
      respondent_management: { label: 'Respondent Management' },
      insights_analytics: { label: 'Insights & Analytics' },
      my_response: { label: 'My Response' },
    },
    contribute: {
      // Contributor - can edit data
      respondent_management: { label: 'Respondent Management' },
      insights_analytics: { label: 'Insights & Analytics' },
      my_response: { label: 'My Response' },
      data_entry: { label: 'Data Entry' },
      questionnaire: { label: 'Questionnaire' },
    },
    manage: {
      // Manager - can approve and report
      respondent_management: { label: 'Respondent Management' },
      insights_analytics: { label: 'Insights & Analytics' },
      my_response: { label: 'My Response' },
      data_entry: { label: 'Data Entry' },
      questionnaire: { label: 'Questionnaire' },
      approvals: { label: 'Approvals' },
      reports: { label: 'Reports' },
    },
    full: {
      // Full access - admin level
      respondent_management: { label: 'Respondent Management' },
      insights_analytics: { label: 'Insights & Analytics' },
      my_response: { label: 'My Response' },
      data_entry: { label: 'Data Entry' },
      questionnaire: { label: 'Questionnaire' },
      approvals: { label: 'Approvals' },
      reports: { label: 'Reports' },
      team_permissions: { label: 'Team & Permissions' },
      settings: { label: 'Settings' },
    },
  };

  return mappings[level] || mappings.contribute; // Default to contributor level
}

/**
 * Map form data for submission - applies any necessary transformations
 * E.g., convert simplified permission level to hierarchical permissions
 */
export function mapDataForSubmission(
  formId: string,
  data: Record<string, any>
): Record<string, any> {
  const result = { ...data };

  // For role creation form: map simplified permissions to hierarchical
  if (formId === 'create_role_form' && result.permissions) {
    result.permissions = mapSimplifiedPermissionsToHierarchical(result.permissions);
  }

  return result;
}

/**
 * Check if an AI response contains a submit action
 * Returns true if the response includes the JSON submit action
 */
export function containsSubmitAction(aiMessage: string): boolean {
  return aiMessage.includes('{"__action":"submit"');
}

/**
 * Extract submit data from AI response
 * The AI returns: {"__action":"submit","data":{...}}
 */
export function extractSubmitData(aiMessage: string): Record<string, any> | null {
  try {
    const jsonMatch = aiMessage.match(/\{"__action":"submit"[^}]*"data":\{[^}]*\}\}/);
    if (!jsonMatch) return null;

    const parsed = JSON.parse(jsonMatch[0]);
    return parsed.data || null;
  } catch (error) {
    console.error('[aiFormService] Error extracting submit data:', error);
    return null;
  }
}
